import type { Config } from 'server-schemas';
import type { FeatureFlags } from 'server-schemas/configs';
import { DATADOME_TELEMETRIC_FLAG_KEY, HUMAN_SECURITY_FLAG_KEY } from 'server-schemas/configs/constants';
import type { SupportedLanguages } from 'server-schemas/configs/feature-flags.types';
import { cookieNames, headerNames, readCookie } from '../../cookie-utils';
import { ClientSideError, ErrorRecovery } from '../../error/error-utils';

import { generateTransactionGUID, getGuid } from '../../correlation';
import { getFlagValueByKeyAndLanguage } from '../../featureFlags';
let TIMEOUT_BFF_FETCH: number; // timeout for all BFF endpoints

let baseUrls: Config.BaseUrls = {
  basket: '/',
  bundles: '/',
  search: '/',
  lookAndBook: '/'
};
let extraHeaders: Record<string, string> = {};

export const setBffConfig = (config: {
  baseUrls: Config.BaseUrls;
  headers: {
    [key: string]: string;
  };
  timeout: number;
}) => {
  baseUrls = config.baseUrls;
  extraHeaders = config.headers;
  TIMEOUT_BFF_FETCH = config.timeout;
};

const defaultHeaders = {
  Accept: 'application/json',
  'Content-Type': 'application/json'
};

export const FORBIDDEN_STATUS = 403;

const isForbiddenResponseWithActiveChallengeFlag = (status: number, featureFlagKey: keyof FeatureFlags): boolean => {
  const language = extraHeaders[headerNames.languageCode] as SupportedLanguages;

  const featureFlagValue = getFlagValueByKeyAndLanguage(featureFlagKey, language);

  return status === FORBIDDEN_STATUS && featureFlagValue === true;
};

export async function fetchBff<ReturnType>(
  service: keyof Config.BaseUrls,
  path: string,
  options: RequestInit
): Promise<ReturnType> {
  const url = `${baseUrls[service]}${path}`;
  // We need to read the XSRF cookie every time and not initialise it once on startup
  // since the XSRF token is unique to each basket session and will update when a basket session expires or user creates a new basket

  try {
    const xsrfCookie = readCookie(cookieNames.xsrf.funnel.server);
    if (extraHeaders && xsrfCookie) {
      extraHeaders[headerNames.funnelClientXsrf] = xsrfCookie;
    }
  } catch (e) {
    console.error('BFF fetch - error reading XSRF cookie:', e);
    throw e;
  }

  try {
    const response = await fetch(url, {
      signal: AbortSignal.timeout(Number(TIMEOUT_BFF_FETCH)),
      ...options,
      headers: {
        ...extraHeaders,
        ...defaultHeaders,
        ...options.headers,
        [headerNames.transactionId]: generateTransactionGUID(getGuid())
      },
      cache: 'no-store'
    });

    if (response.status === 428) {
      throw new ClientSideError({
        message: 'Awaiting Recaptcha Modal',
        recovery: ErrorRecovery.DO_NOTHING,
        showCorrelation: false
      });
    }

    if (isForbiddenResponseWithActiveChallengeFlag(response.status, DATADOME_TELEMETRIC_FLAG_KEY)) {
      throw new ClientSideError({
        message: 'Awaiting Datadome challenge',
        recovery: ErrorRecovery.DO_NOTHING,
        showCorrelation: false
      });
    }

    if (isForbiddenResponseWithActiveChallengeFlag(response.status, HUMAN_SECURITY_FLAG_KEY)) {
      throw new ClientSideError({
        message: 'Awaiting human js challenge',
        recovery: ErrorRecovery.DO_NOTHING,
        showCorrelation: false
      });
    }

    const data = await response.json();
    if (data.error || data.errors) {
      throw data.error || data.errors;
    }
    return data;
  } catch (error: unknown) {
    if (error instanceof Error && error.name == 'AbortError') {
      throw new ClientSideError({
        message: 'Request timed out',
        recovery: ErrorRecovery.SHOW_ERROR,
        showCorrelation: true
      });
    }
    console.error('BFF fetch error:', error instanceof Error ? error.message : JSON.stringify(error));
    throw error;
  }
}
